<template>
  <div class="profile-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <h2>Mon Profil</h2>
        <p>Gérez vos informations personnelles et paramètres de compte</p>
      </div>
    </div>

    <div class="profile-container">
      <!-- Profile Card -->
      <div class="profile-card">
        <div class="profile-header">
          <div class="profile-avatar">
            <i class="fas fa-user"></i>
          </div>
          <div class="profile-info">
            <h3>{{ user?.prenom }} {{ user?.nom }}</h3>
            <p class="profile-role">{{ user?.role }}</p>
            <div class="profile-meta">
              <div class="meta-item">
                <i class="fas fa-calendar-alt"></i>
                Membre depuis {{ formatDate(user?.dateCreation) }}
              </div>
              <div class="meta-item">
                <i class="fas fa-clock"></i>
                Dernière connexion {{ formatTime(user?.dernierConnexion) }}
              </div>
            </div>
          </div>
          <div class="profile-status">
            <span class="status-badge status-active">
              <i class="fas fa-check-circle"></i>
              Compte Actif
            </span>
          </div>
        </div>
      </div>

      <!-- Profile Form -->
      <div class="profile-form-card">
        <div class="card-header">
          <h3>Informations Personnelles</h3>
          <button 
            v-if="!editMode"
            @click="enableEditMode"
            class="btn btn-primary btn-sm"
          >
            <i class="fas fa-edit"></i>
            Modifier
          </button>
        </div>

        <form @submit.prevent="handleSubmit" class="card-body">
          <div class="form-row">
            <div class="form-group">
              <label for="prenom">Prénom</label>
              <input
                id="prenom"
                v-model="form.prenom"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.prenom }"
                :readonly="!editMode"
                placeholder="Prénom"
              />
              <div v-if="errors.prenom" class="form-error">
                {{ errors.prenom }}
              </div>
            </div>

            <div class="form-group">
              <label for="nom">Nom</label>
              <input
                id="nom"
                v-model="form.nom"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.nom }"
                :readonly="!editMode"
                placeholder="Nom de famille"
              />
              <div v-if="errors.nom" class="form-error">
                {{ errors.nom }}
              </div>
            </div>
          </div>

          <div class="form-group">
            <label for="email">Adresse email</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="form-control"
              :class="{ 'is-invalid': errors.email }"
              :readonly="!editMode"
              placeholder="exemple@email.com"
            />
            <div v-if="errors.email" class="form-error">
              {{ errors.email }}
            </div>
          </div>

          <div class="form-group">
            <label for="telephone">Téléphone</label>
            <input
              id="telephone"
              v-model="form.telephone"
              type="tel"
              class="form-control"
              :class="{ 'is-invalid': errors.telephone }"
              :readonly="!editMode"
              placeholder="+33 1 23 45 67 89"
            />
            <div v-if="errors.telephone" class="form-error">
              {{ errors.telephone }}
            </div>
          </div>

          <div class="form-group">
            <label for="adresse">Adresse</label>
            <textarea
              id="adresse"
              v-model="form.adresse"
              class="form-control"
              :class="{ 'is-invalid': errors.adresse }"
              :readonly="!editMode"
              placeholder="Adresse complète"
              rows="3"
            ></textarea>
            <div v-if="errors.adresse" class="form-error">
              {{ errors.adresse }}
            </div>
          </div>

          <div class="form-group">
            <label>Rôle</label>
            <div class="role-display">
              <span class="badge" :class="getRoleBadgeClass(user?.role)">
                {{ user?.role }}
              </span>
              <span class="role-description">
                {{ getRoleDescription(user?.role) }}
              </span>
            </div>
          </div>

          <div v-if="editMode" class="form-actions">
            <button 
              type="button" 
              @click="cancelEdit"
              class="btn btn-secondary"
            >
              <i class="fas fa-times"></i>
              Annuler
            </button>
            <button 
              type="submit"
              :disabled="loading"
              class="btn btn-primary"
            >
              <span v-if="loading" class="loading-spinner"></span>
              <i v-else class="fas fa-save"></i>
              {{ loading ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
          </div>

          <div v-if="submitError" class="submit-error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ submitError }}
          </div>
        </form>
      </div>

      <!-- Security Section -->
      <div class="security-card">
        <div class="card-header">
          <h3>Sécurité</h3>
        </div>
        <div class="card-body">
          <div class="security-item">
            <div class="security-info">
              <h4>Mot de passe</h4>
              <p>Dernière modification il y a plus de 30 jours</p>
            </div>
            <button 
              @click="showChangePasswordModal = true"
              class="btn btn-secondary btn-sm"
            >
              <i class="fas fa-key"></i>
              Changer
            </button>
          </div>

          <div class="security-item">
            <div class="security-info">
              <h4>Sessions actives</h4>
              <p>Gérez vos sessions de connexion</p>
            </div>
            <button class="btn btn-secondary btn-sm">
              <i class="fas fa-list"></i>
              Voir les sessions
            </button>
          </div>
        </div>
      </div>

      <!-- Activity Card -->
      <div class="activity-card">
        <div class="card-header">
          <h3>Activité Récente</h3>
        </div>
        <div class="card-body">
          <div class="activity-list">
            <div class="activity-item">
              <div class="activity-icon">
                <i class="fas fa-sign-in-alt"></i>
              </div>
              <div class="activity-content">
                <div class="activity-text">Connexion réussie</div>
                <div class="activity-time">{{ formatTime(user?.dernierConnexion) }}</div>
              </div>
            </div>
            
            <div class="activity-item">
              <div class="activity-icon">
                <i class="fas fa-user-edit"></i>
              </div>
              <div class="activity-content">
                <div class="activity-text">Profil mis à jour</div>
                <div class="activity-time">Il y a 2 jours</div>
              </div>
            </div>
            
            <div class="activity-item">
              <div class="activity-icon">
                <i class="fas fa-key"></i>
              </div>
              <div class="activity-content">
                <div class="activity-text">Mot de passe modifié</div>
                <div class="activity-time">Il y a 1 semaine</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Change Password Modal -->
    <ChangePasswordModal 
      v-if="showChangePasswordModal"
      @close="showChangePasswordModal = false"
      @password-changed="handlePasswordChanged"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import ChangePasswordModal from '../components/ChangePasswordModal.vue'

export default {
  name: 'Profile',
  components: {
    ChangePasswordModal
  },
  setup() {
    const store = useStore()
    const toast = useToast()

    const editMode = ref(false)
    const loading = ref(false)
    const submitError = ref('')
    const showChangePasswordModal = ref(false)

    const form = reactive({
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      adresse: ''
    })

    const originalForm = reactive({})

    const errors = reactive({
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      adresse: ''
    })

    const user = computed(() => store.getters['auth/user'])

    const initializeForm = () => {
      if (user.value) {
        form.prenom = user.value.prenom || ''
        form.nom = user.value.nom || ''
        form.email = user.value.email || ''
        form.telephone = user.value.telephone || ''
        form.adresse = user.value.adresse || ''

        // Save original values
        Object.assign(originalForm, form)
      }
    }

    const enableEditMode = () => {
      editMode.value = true
    }

    const cancelEdit = () => {
      // Restore original values
      Object.assign(form, originalForm)
      editMode.value = false
      submitError.value = ''
      
      // Clear errors
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })
    }

    const validateForm = () => {
      // Reset errors
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })

      let isValid = true

      // Prénom validation
      if (!form.prenom.trim()) {
        errors.prenom = 'Le prénom est requis'
        isValid = false
      } else if (form.prenom.trim().length < 2) {
        errors.prenom = 'Le prénom doit contenir au moins 2 caractères'
        isValid = false
      }

      // Nom validation
      if (!form.nom.trim()) {
        errors.nom = 'Le nom est requis'
        isValid = false
      } else if (form.nom.trim().length < 2) {
        errors.nom = 'Le nom doit contenir au moins 2 caractères'
        isValid = false
      }

      // Email validation
      if (!form.email.trim()) {
        errors.email = 'L\'email est requis'
        isValid = false
      } else if (!/\S+@\S+\.\S+/.test(form.email)) {
        errors.email = 'Format d\'email invalide'
        isValid = false
      }

      // Phone validation (optional)
      if (form.telephone && form.telephone.trim()) {
        const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/
        if (!phoneRegex.test(form.telephone.trim())) {
          errors.telephone = 'Format de téléphone invalide (ex: +33 1 23 45 67 89)'
          isValid = false
        }
      }

      return isValid
    }

    const handleSubmit = async () => {
      submitError.value = ''

      if (!validateForm()) {
        return
      }

      loading.value = true

      try {
        await store.dispatch('auth/updateProfile', {
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          email: form.email.trim().toLowerCase(),
          telephone: form.telephone,
          adresse: form.adresse
        })

        // Update original form values
        Object.assign(originalForm, form)
        
        editMode.value = false
        toast.success('Profil mis à jour avec succès')
      } catch (error) {
        console.error('Erreur mise à jour profil:', error)
        
        // Gestion des erreurs de validation du serveur
        if (error.response?.data?.errors) {
          const serverErrors = error.response.data.errors
          serverErrors.forEach(err => {
            if (err.param && errors[err.param] !== undefined) {
              errors[err.param] = err.msg
            }
          })
          submitError.value = error.response.data.message || 'Erreurs de validation'
        } else {
          submitError.value = error.message || 'Erreur lors de la mise à jour'
        }
        
        toast.error(submitError.value)
      } finally {
        loading.value = false
      }
    }

    const handlePasswordChanged = () => {
      showChangePasswordModal.value = false
      toast.success('Mot de passe modifié avec succès')
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'Non disponible'
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const formatTime = (dateString) => {
      if (!dateString) return 'Jamais'
      const date = new Date(dateString)
      const now = new Date()
      const diff = now - date
      
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      
      if (minutes < 1) return 'À l\'instant'
      if (minutes < 60) return `Il y a ${minutes} min`
      if (hours < 24) return `Il y a ${hours}h`
      if (days < 7) return `Il y a ${days} jour(s)`
      
      return date.toLocaleDateString('fr-FR')
    }

    const getRoleBadgeClass = (role) => {
      const classes = {
        'Admin': 'badge-primary',
        'Responsable Planning': 'badge-info',
        'Commercial': 'badge-success',
        'Magasinier': 'badge-warning',
        'Technicien': 'badge-secondary'
      }
      return classes[role] || 'badge-secondary'
    }

    const getRoleDescription = (role) => {
      const descriptions = {
        'Admin': 'Accès complet à toutes les fonctionnalités',
        'Responsable Planning': 'Gestion des plannings et des équipes',
        'Commercial': 'Gestion des clients et des ventes',
        'Magasinier': 'Gestion des stocks et des inventaires',
        'Technicien': 'Accès aux outils techniques et maintenance'
      }
      return descriptions[role] || 'Rôle utilisateur standard'
    }

    onMounted(() => {
      initializeForm()
    })

    return {
      editMode,
      loading,
      submitError,
      showChangePasswordModal,
      form,
      errors,
      user,
      enableEditMode,
      cancelEdit,
      handleSubmit,
      handlePasswordChanged,
      formatDate,
      formatTime,
      getRoleBadgeClass,
      getRoleDescription
    }
  }
}
</script>

<style scoped>
.profile-page {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--light-gray);
}

.page-header h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.5rem;
}

.page-header p {
  color: var(--medium-gray);
  margin: 0;
}

.profile-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.profile-card,
.profile-form-card,
.security-card,
.activity-card {
  background: var(--white);
  border-radius: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.profile-card {
  grid-column: 1 / -1;
}

.profile-header {
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  backdrop-filter: blur(10px);
}

.profile-info {
  flex: 1;
}

.profile-info h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.profile-role {
  font-size: 1rem;
  opacity: 0.9;
  margin-bottom: 1rem;
}

.profile-meta {
  display: flex;
  gap: 2rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  opacity: 0.8;
}

.profile-status {
  text-align: right;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.status-badge.status-active {
  color: var(--white);
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--light-gray);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--dark-gray);
}

.card-body {
  padding: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control:read-only {
  background-color: #f8fafc;
  cursor: default;
}

.role-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
}

.role-description {
  color: var(--medium-gray);
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--light-gray);
}

.submit-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
  padding: 0.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.security-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid var(--light-gray);
}

.security-item:last-child {
  border-bottom: none;
}

.security-info h4 {
  font-size: 1rem;
  font-weight: 500;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
}

.security-info p {
  font-size: 0.875rem;
  color: var(--medium-gray);
  margin: 0;
}

.activity-list {
  max-height: 300px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--light-gray);
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: var(--white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.activity-content {
  flex: 1;
}

.activity-text {
  font-size: 0.875rem;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
}

.activity-time {
  font-size: 0.75rem;
  color: var(--medium-gray);
}

/* Responsive */
@media (max-width: 1024px) {
  .profile-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .profile-header {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .profile-meta {
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .security-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
</style>
